import React, {
  useEffect, useState, useMemo,
} from 'react';
import propTypes from 'prop-types';

import {
  assembleFloorPlanCoreNavBtnsData,
  assembleFloorPlanHeaderTextData,
  showPricingTrigger,
  createBedBathSqFtTableData,
} from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanHelpers';
import { useAppStore, useConfigStore } from '../../stores';
import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import useDetailViewTracking from '../../hooks/useDetailViewTracking';

import { createSkeletonItem } from '../Skeleton/SkeletonHelpers';

import DetailTableInfo from '../Detail/DetailTable';
import DetailHeader from '../Detail/DetailHeader';
import FloorPlanDetailCore from './FloorPlanDetailCore';
import FavoriteButton from '../Personalization/Favorite/FavoriteButton';

import '../Detail/Detail.scss';

const FloorPlanDetail = ({
  uuid,
  parent,
  data,
  existingInventoryPhrase,
  setModalNavBtns,
  setModalHeaderData,
  hasInternalHeader,
  navClass,
  trackPibViews,
}) => {
  useDetailViewTracking({
    type: 'floor plan',
    nid: data?.nid,
    enabled: trackPibViews,
  });

  const [floorPlanData, setFloorPlanData] = useState(null);
  const [detailHeaderNavBtns, setDetailHeaderNavBtns] = useState(null);
  const [detailHeaderText, setDetailHeaderText] = useState(null);
  const [tableInfo, setTableInfo] = useState(null);

  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const pricingTriggered = useAppStore((state) => state.pricingTriggered);
  const setPricingTriggered = useAppStore((state) => state.setPricingTriggered);
  const mode = useAppStore((state) => state.settings?.mode);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const { platformShowPricing, platformShowPricingInitially } = featureFlags || {};
  const soldOutPhrase = useConfigStore((state) => state.config?.soldOutPhrase);

  const {
    title,
    description,
    feeData,
    hasFees: minimalHasFees,
  } = floorPlanData || data || {};

  // hasFees: Use feeData.hasFees if feeData exists (full data loaded), otherwise use minimal data
  // This ensures we don't show "more" button when feeData exists but has no fees
  const hasFees = feeData ? feeData.hasFees : minimalHasFees;

  const components = useMemo(() => (personalizationActive ? [<FavoriteButton
    type="floor_plan"
    uuid={uuid}
    hasShadow={false}
    className="detailHeader__favorite"
  />] : []), [personalizationActive, uuid]);

  // Get floor plan data - only run once per uuid
  useEffect(() => {
    if (uuid) {
      const fetchData = async () => {
        const auth = getAuthClient();
        const params = mode ? { mode } : {};
        const url = buildYourTourApiUrl(`/your_tour_api/floor_plan/${uuid}`, params);
        const rawData = await auth.fetchWithAuthentication(url, 'GET', null);
        // Preserve inventory data from initial data prop (PIB passes this from optimized endpoint)
        // The floor_plan API doesn't include inventory data, so we need to keep it from the original data
        const mergedData = {
          ...rawData,
          remainingInventory: data?.remainingInventory ?? rawData?.remainingInventory,
          soldOut: data?.soldOut ?? rawData?.soldOut,
          inventoryPhrase: data?.inventoryPhrase ?? rawData?.inventoryPhrase,
        };
        setFloorPlanData(mergedData);
      };
      fetchData();
    } else if (data && !floorPlanData) {
      // When data is already available (e.g., from FloorPlansPage), use it immediately
      // Only set if we don't have data yet
      setFloorPlanData(data);
    }
    // Only run when uuid changes, or initially if data is provided
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  // PIB needs to show beds/bath, YT does not.
  // Use minimal data immediately if available, then update when full data loads
  useEffect(() => {
    const dataToUse = floorPlanData || data;

    // If we have no data yet, show skeleton listData (4 items)
    if (!dataToUse) {
      const skeletonListData = [];
      for (let i = 0; i < 4; i++) {
        skeletonListData.push({
          key: `skeleton-list-${i}`,
          note: createSkeletonItem('div', 'detailTable__itemNote', 'full', true),
        });
      }
      setTableInfo(skeletonListData);
    } else {
      const rawTableInfo = createBedBathSqFtTableData(dataToUse);
      setTableInfo(rawTableInfo);
    }
    // Only re-run when floorPlanData changes, not when data prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlanData]);

  // Assemble nav btns.
  // Only update when we have actual data, don't overwrite pre-built nav buttons with null
  useEffect(() => {
    if (floorPlanData) {
      const rawNavBtns = assembleFloorPlanCoreNavBtnsData(floorPlanData);
      if (hasInternalHeader) {
        setDetailHeaderNavBtns(rawNavBtns);
      } else if (setModalNavBtns) {
        setModalNavBtns(rawNavBtns);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlanData,
    hasInternalHeader]);

  // Assemble header data.
  // Use minimal data immediately if available, then update when full data loads
  useEffect(() => {
    const dataToUse = floorPlanData || data;
    const rawShowPricingTrigger = showPricingTrigger(
      pricingTriggered,
      platformShowPricing,
      platformShowPricingInitially,
      hasFees,
    );
    const rawDetailHeaderText = assembleFloorPlanHeaderTextData(
      dataToUse,
      null,
      existingInventoryPhrase,
      hasInternalHeader,
      rawShowPricingTrigger,
      setPricingTriggered,
      components,
      soldOutPhrase,
    );
    if (hasInternalHeader) {
      setDetailHeaderText(rawDetailHeaderText);
    } else if (setModalHeaderData) {
      setModalHeaderData(rawDetailHeaderText);
    }
    // Only re-run when key values change, not when data/components references change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingTriggered,
    platformShowPricing,
    platformShowPricingInitially,
    hasFees,
    floorPlanData,
    existingInventoryPhrase,
    hasInternalHeader,
    personalizationActive,
    soldOutPhrase,
    uuid]);

  // No longer need to track modal loading state - we have pre-built nav buttons
  // so scroll can be enabled immediately

  return (
    <div>

      {hasInternalHeader && (
        <DetailHeader
          title={title}
          data={detailHeaderText}
          navBtns={detailHeaderNavBtns}
          navClassName={navClass}
        />
      )}

      {/* Display only basic info - beds/baths/sqft without fees */}
      {/* Show immediately with minimal data, update when full data loads */}
      <DetailTableInfo
        id="info-scroll"
        listData={tableInfo}
        tableDescription={description || data?.description}
        className="detailSection"
      />

      <FloorPlanDetailCore
        data={floorPlanData}
        minimalData={data}
        parent={parent}
        contentType="floor_plan"
        isLoadingPhotoGallery={floorPlanData === null && !!data?.photoGalleryUuid}
        isLoadingVideo={floorPlanData === null && !!data?.videoUuid}
        isLoadingVirtualTour={floorPlanData === null && !!data?.virtualTourUuid}
      />

    </div>
  );
};

FloorPlanDetail.propTypes = {
  uuid: propTypes.string,
  parent: propTypes.string,
  data: propTypes.shape({
    title: propTypes.string,
    levelsOfCareName: propTypes.string,
    floorPlanCategoryName: propTypes.string,
    fees: propTypes.arrayOf(
      propTypes.shape({
        label: propTypes.string,
        data: propTypes.string,
        note: propTypes.string,
      }),
    ),
    feesData: propTypes.object,
    description: propTypes.string,
    floorplan2d: propTypes.shape({
      modal: propTypes.string,
    }),
    floorplan3d: propTypes.shape({
      modal: propTypes.string,
    }),
    roomData: propTypes.arrayOf(
      propTypes.shape({
        label: propTypes.string,
      }),
    ),
    threeSixties: propTypes.arrayOf(
      propTypes.shape({
        id: propTypes.string,
        image: propTypes.string,
      }),
    ),
    furnitureConfiguratorUrl: propTypes.string,
    video: propTypes.shape({
      videoId: propTypes.string,
      service: propTypes.string,
    }),
    photoGalleryImages: propTypes.arrayOf(
      propTypes.shape({
        id: propTypes.string,
        // Supports both new API format (string) and legacy format (object)
        image: propTypes.oneOfType([
          propTypes.string,
          propTypes.shape({
            modal: propTypes.string,
            fullScreen: propTypes.string,
          }),
        ]),
        caption: propTypes.string,
      }),
    ),
    finishCollection: propTypes.shape({
      id: propTypes.string,
      title: propTypes.string,
      options: propTypes.arrayOf(
        propTypes.shape({
          title: propTypes.string,
          fields: propTypes.arrayOf(
            propTypes.shape({
              label: propTypes.string,
              data: propTypes.string,
            }),
          ),
        }),
      ),
    }),
    downloadablePdf: propTypes.string,
  }),
  existingInventoryPhrase: propTypes.string,
  setModalNavBtns: propTypes.func,
  setModalHeaderData: propTypes.func,
  hasInternalHeader: propTypes.bool,
  navClass: propTypes.string,
  trackPibViews: propTypes.bool,
};

FloorPlanDetail.defaultProps = {
  uuid: null,
  parent: null,
  data: null,
  existingInventoryPhrase: null,
  setModalNavBtns: null,
  setModalHeaderData: null,
  hasInternalHeader: null,
  navClass: null,
  trackPibViews: false,
};

export default FloorPlanDetail;
